import * as Api from '@ViewModels';
import { brandPrimaryHover, success } from '../../styles/colors';
import { AutomationsIcon } from '../svgs/icons/AutomationsIcon';
import { EmailIcon } from '../svgs/icons/EmailIcon';
import { ExportIcon } from '../svgs/icons/ExportIcon';
import { PostageIcon } from '../svgs/icons/PostageIcon';
import { TagIcon } from '../svgs/icons/TagIcon';
import { TextingIcon } from '../svgs/icons/TextingIcon';
import { IActionButton } from './ActionBarButtonTypes';
import { styleSheet } from './styles';

interface IUseActionButtonsParams {
	selectionState: Api.ESelectionState;
	getMatchingContacts: () => Promise<Api.IProjectedContact[]>;
	onSendEmailClicked: () => void;
	onSendCardClicked: () => void;
	onSendTextClicked: () => void;
	onAddTagClicked: () => void;
	onExport?: () => void;
	setContactSelectionForAutomation: (selection: any) => void;
	setIsAutomationOpen: (isOpen: boolean) => void;
	canShowAutomations?: boolean;
	canShowCards?: boolean;
	canSendTextingCampaign?: boolean;
	actionButtonsDisabled?: boolean;
}

export const useActionButtons = ({
	selectionState,
	getMatchingContacts,
	onSendEmailClicked,
	onSendCardClicked,
	onSendTextClicked,
	onAddTagClicked,
	onExport,
	setContactSelectionForAutomation,
	setIsAutomationOpen,
	canShowAutomations = false,
	canShowCards = false,
	canSendTextingCampaign = false,
	actionButtonsDisabled = false,
}: IUseActionButtonsParams): IActionButton[] => {
	const isActive = selectionState !== 'none' && !actionButtonsDisabled;

	const buttons: IActionButton[] = [
		{
			key: 'sendEmail',
			icon: <EmailIcon fillColor='#EA8160' />,
			children: 'Send Email',
			onClick: onSendEmailClicked,
			disabled: !isActive || actionButtonsDisabled,
			styleClass: styleSheet.sendEmail,
		},
	];

	if (canSendTextingCampaign) {
		buttons.push({
			key: 'sendText',
			icon: <TextingIcon fill='#6E27A3' />,
			children: 'Send Text',
			onClick: onSendTextClicked,
			disabled: selectionState === 'none' || actionButtonsDisabled,
			styleClass: styleSheet.sendText,
		});
	}

	if (canShowCards) {
		buttons.push({
			key: 'sendCard',
			icon: <PostageIcon fillColor='#00AAE8' />,
			children: 'Send Card',
			onClick: onSendCardClicked,
			disabled: !isActive || actionButtonsDisabled,
			styleClass: styleSheet.sendCard,
		});
	}

	buttons.push({
		key: 'tag',
		icon: <TagIcon fillColor={brandPrimaryHover} />,
		children: 'Tag',
		onClick: onAddTagClicked,
		disabled: !isActive,
		styleClass: styleSheet.tag,
	});

	if (canShowAutomations) {
		buttons.push({
			key: 'startAutomation',
			icon: <AutomationsIcon fillColor={success} />,
			children: 'Start Automations',
			onClick: async () => {
				const resolveContacts = await getMatchingContacts();
				setContactSelectionForAutomation({
					includeContactIds: resolveContacts.map(c => c.id),
				});
				setIsAutomationOpen(true);
			},
			disabled: !isActive || actionButtonsDisabled,
			styleClass: styleSheet.export,
		});
	}

	if (onExport) {
		buttons.push({
			key: 'export',
			icon: <ExportIcon fillColor={success} />,
			children: 'Export',
			onClick: onExport,
			disabled: !isActive || actionButtonsDisabled,
			styleClass: styleSheet.export,
		});
	}

	return buttons;
};
